/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons/index.js";
import AmountToWords from "../../../../components/_utils/AmountToWords.js";
import CommonAssignLabelPopup from "../../../../components/_utils/CommonPopups/CommonAssignLabelPopup/index.jsx";
import SmallPopup from "../../../../components/_utils/CommonSmallPopup/index.jsx";
import PrimaryStepper from "../../../../components/_utils/CommonSteppers/PrimaryStepper";
import CommonToast from "../../../../components/_utils/CommonToast/index.jsx";
import TopNavComponent from "../../../../components/_utils/CommonTopNav/TopNavComponent/index.jsx";
import BeneficiaryAccountViewAllPopup from "../../../../components/Accounts/BeneficiaryAccount/BeneficiaryAccountViewAllPopup/index.jsx";
import Loader from "../../../../components/Loader/Index.jsx";
import ScheduleTxnOccurrencesPopup from "../../../../components/ScheduleTransactions/ScheduleTxnOccurrencesPopup/index.jsx";
import ScheduleTransactionStepAdditionalDetails from "../../../../components/Transactions/ScheduleBeneficiaryTransaction/ScheduleBeneficiaryTransactionCreate/ScheduleTransactionStepAdditionalDetails";
import ScheduleTransactionStepAmount from "../../../../components/Transactions/ScheduleBeneficiaryTransaction/ScheduleBeneficiaryTransactionCreate/ScheduleTransactionStepAmount";
import ScheduleTransactionStepConfirm from "../../../../components/Transactions/ScheduleBeneficiaryTransaction/ScheduleBeneficiaryTransactionCreate/ScheduleTransactionStepConfirm";
import ScheduleTransactionStepFrequency from "../../../../components/Transactions/ScheduleBeneficiaryTransaction/ScheduleBeneficiaryTransactionCreate/ScheduleTransactionStepFrequency";
import ScheduleTransactionStepSelectAccount from "../../../../components/Transactions/ScheduleBeneficiaryTransaction/ScheduleBeneficiaryTransactionCreate/ScheduleTransactionStepSelectAccount";
import ScheduleBeneficiaryTransactionViewAllPopup from "../../../../components/Transactions/ScheduleBeneficiaryTransaction/ScheduleBeneficiaryTransactionViewAllPopup/index.jsx";
import { setHideBreadCrumb } from "../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import {
  getAllCategories,
  uploadFile,
} from "../../../../redux/features/sharedResources/sharedResources";
import {
  scheduledBeneficiaryCreate,
  scheduledBeneficiaryOccurrences,
  scheduledBeneficiaryViewAll,
} from "../../../../redux/features/transactions/scheduleBeneficiaryTransactions/index.js";
import {
  GlobalConfiguration,
  IntervalConfiguration,
  PaginationConfiguration,
  PaymentMethodAnimationConfiguration,
} from "../../../../utils/configurationProvider";
import {
  FREQUENCY_OPTIONS,
  MONTHS,
  SHORT_MONTHS,
  SLOT_DATA,
  WEEKDAYS,
} from "../../../../utils/dateConstants.js";
import globalText from "../../../../utils/globalText";
import Validation from "../../../../utils/validation.js";

/*                         CONSTANTS & PURE HELPERS                           */

const {
  lengthCheckZero: ZERO,
  lengthCheckOne: ONE,
  lengthCheckFive: FIVE,
} = GlobalConfiguration;

const STEP = {
  ZERO: GlobalConfiguration.lengthCheckZero,
  ONE: GlobalConfiguration.lengthCheckOne,
  TWO: GlobalConfiguration.lengthCheckTwo,
  THIRD: GlobalConfiguration.lengthCheckThree,
  FOUR: GlobalConfiguration.lengthCheckFour,
};
const LAST_STEP_INDEX = STEP.FOUR;

const RTGS_MIN_AMOUNT = 200000;
const MAX_NUMERIC_DIGITS = 15;
const HALF_YEARLY_LABEL = "Half yearly";
const HALF_YEARLY_KEY = "half_yearly";

// categories can't be un-assigned from this flow
const DISABLE_CATEGORIES = true;

const COMMON_STEPPER_CONFIG = {
  previousEnableBgColor: "var(--primary-nav-act)",
  previousDisableBgColor: "var(--text-disabled)",
  previousEnableTextColor: "var(--text-primary)",
  previousHoverOption: "white",
  nextDisableTextColor: "var(--text-primary)",
  nextHoverOption: "black",
};

// how far the default "end date" is from the start date, per frequency
const FREQUENCY_END_DATE_OFFSET = {
  Weekly: { unit: "date", amount: 7 },
  Fortnightly: { unit: "date", amount: 14 },
  Monthly: { unit: "month", amount: 1 },
  Quarterly: { unit: "month", amount: 3 },
  [HALF_YEARLY_LABEL]: { unit: "month", amount: 6 },
  Yearly: { unit: "year", amount: 1 },
};

const omit = (obj, ...keys) =>
  Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key)),
  );

const uniqueList = (...lists) => [
  ...new Set(lists.flatMap((list) => list || [])),
];

const readSessionJson = (key, fallback = []) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const isValidHttpUrl = (url) =>
  /^(https?:\/\/)([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(:[0-9]{1,5})?(\/.*)?$/.test(
    url,
  );

/** Splits a flat label list into owner labels / other labels. */
const splitLabels = (allLabels = [], ownerLabels = []) => ({
  ownerLabels: allLabels.filter((label) => ownerLabels.includes(label)),
  labels: allLabels.filter((label) => !ownerLabels.includes(label)),
});

/** Amount -> words, singularising "rupees" for amounts above 0 and up to 1. */
const getAmountWords = (amount) => {
  const amountNumber = parseFloat(amount);
  const words = AmountToWords(amountNumber);
  return amountNumber > ZERO && amountNumber <= ONE
    ? words.replace("rupees", "rupee")
    : words;
};

/** "Half yearly" -> "half_yearly", "Monthly" -> "monthly". */
const toApiFrequency = (frequency) =>
  frequency === HALF_YEARLY_LABEL
    ? HALF_YEARLY_KEY
    : (frequency || "").toLowerCase();

/** "YYYY-MM-DD" is parsed as a LOCAL date (new Date(str) would treat it as UTC). */
const parseToDateOrNull = (value) => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const candidate = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value);
  return isNaN(candidate) ? null : candidate;
};

/** Date -> "YYYY-MM-DD" in the user's local timezone. */
const toLocalIsoDate = (date) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (isNaN(parsed)) return "";
  return new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

/** Date -> "5 Jan, 2026" (display format). */
const formatDisplayDate = (date) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (isNaN(parsed)) return "";
  return `${parsed.getDate()} ${parsed.toLocaleString("default", { month: "short" })}, ${parsed.getFullYear()}`;
};

const isToday = (date) => {
  if (!date) return false;
  const today = new Date();
  const target = new Date(date);
  return (
    today.getDate() === target.getDate() &&
    today.getMonth() === target.getMonth() &&
    today.getFullYear() === target.getFullYear()
  );
};

/** If the schedule starts today, only slots later than the current hour are valid. */
const getAvailableSlots = (startDate) =>
  isToday(startDate)
    ? SLOT_DATA.filter(
        (slot) => Number(slot.value.split(":")[0]) > new Date().getHours(),
      )
    : SLOT_DATA;

const getFrequencyEndDate = (startDate, frequency) => {
  if (!startDate) return new Date();

  const endDate = new Date(startDate);
  const { unit, amount } = FREQUENCY_END_DATE_OFFSET[frequency] || {
    unit: "date",
    amount: 1,
  };

  if (unit === "date") endDate.setDate(endDate.getDate() + amount);
  if (unit === "month") endDate.setMonth(endDate.getMonth() + amount);
  if (unit === "year") endDate.setFullYear(endDate.getFullYear() + amount);

  return endDate;
};

/** Whether a mandatory field (from mergeCategory) has been filled. */
const isMandatoryFieldFilled = ({ data_type: dataType, value }) => {
  switch (dataType) {
    case "multi_select_list":
      return Array.isArray(value) && value.length > ZERO;
    case "from_year_to_year":
      return value?.from != null && value?.to != null;
    case "month_year":
      return typeof value === "string" && value !== "";
    case "date_range":
      return !!(value?.start_date && value?.end_date);
    case "toggle":
      return value === true;
    case "upload":
      return !!value;
    default:
      return value !== "" && value !== undefined;
  }
};

/**
 * Category config fields + entered values (+ upload responses) -> API payload.
 * `uploadResponses` is only passed when at least one file was actually chosen.
 */
const buildAdditionalDataPayload = (
  fields,
  additionalData,
  uploadResponses,
) => {
  let uploadIndex = ZERO;

  return fields?.map((field) => {
    const { data_type: dataType, name } = field;
    const value = additionalData[name];

    if (value) {
      if (dataType === "numeric") return { ...field, value: Number(value) };

      if (dataType === "upload" && uploadResponses) {
        return {
          ...omit(field, "description"),
          value: uploadResponses[uploadIndex++]?.payload,
        };
      }

      if (dataType === "toggle") {
        return { ...omit(field, "description"), value };
      }

      return { ...field, value };
    }

    return dataType === "toggle" ? omit(field, "description") : field;
  });
};

/** Category config fields + entered values -> mergeCategory (drives validation + confirm step). */
const buildMergeCategory = (fields, additionalData) =>
  fields?.map((field) => {
    const { data_type: dataType, name, mandatory } = field;
    const value = additionalData[name];

    const hasValue =
      value !== undefined &&
      (Array.isArray(value) ? value.length > ZERO : value !== "");

    if (hasValue) {
      return {
        ...field,
        value: dataType === "numeric" ? String(value) : value,
      };
    }

    switch (dataType) {
      case "multi_select_list":
        return { ...field, value: [] };
      case "toggle":
        return { ...field, value: false };
      case "hyperlink":
      case "upload":
        return { ...field, value: "" };
      default:
        return mandatory !== "Y" ? { ...field, value: "" } : field;
    }
  });

/** Raw input from a field component -> value stored in additionalData. */
const toAdditionalDataValue = (dataType, value) => {
  switch (dataType) {
    case "month_year": {
      if (!(value?.month && value?.year)) return "";
      const monthNumber = String(MONTHS.indexOf(value.month) + ONE).padStart(
        2,
        "0",
      );
      return `${monthNumber}/${value.year}`;
    }

    case "from_year_to_year": {
      const toNumberOrNull = (v) =>
        v !== "" && v !== undefined ? Number(v) : null;
      return {
        from: toNumberOrNull(value?.from),
        to: toNumberOrNull(value?.to),
      };
    }

    case "date_range":
      return value?.start_date && value?.end_date
        ? { start_date: value.start_date, end_date: value.end_date }
        : null;

    case "numeric":
      return value === ""
        ? ""
        : Number(String(value).replace(/\D/g, "").slice(0, MAX_NUMERIC_DIGITS));

    case "quantity":
      return Number(value);

    default:
      return typeof value === "string" ? value.replace(/^\s+/, "") : value;
  }
};

/** Pure validation for the "Next" button, one entry per step. */
const getStepValidity = (txnPayLoad) => {
  const {
    beneAccountId,
    ownerId,
    scheduleName,
    selectedCategory: payloadCategory,
    mergeCategory,
    amount,
    settleType,
    frequency,
    occurrences,
    slot,
    transaction_start_date: startDate,
    transaction_end_date: endDate,
  } = txnPayLoad;

  const fields = mergeCategory || [];

  const mandatoryCount = (payloadCategory?.configuration?.data || []).filter(
    (field) => field?.mandatory === "Y",
  ).length;
  const filledMandatoryCount = fields.filter(
    (field) => field?.mandatory === "Y" && isMandatoryFieldFilled(field),
  ).length;

  const hyperlinksValid = fields
    .filter((field) => field?.data_type === "hyperlink" && field?.value !== "")
    .every((field) => isValidHttpUrl(field?.value));

  const amountNumber = Number(amount);
  const isSettleTypeValid =
    (settleType === "neft" && amountNumber <= RTGS_MIN_AMOUNT) ||
    (settleType === "rtgs" && amountNumber > RTGS_MIN_AMOUNT);

  return {
    [STEP.ZERO]:
      !!beneAccountId &&
      !!ownerId &&
      scheduleName?.replace(/\s+/g, "").trim().length >= FIVE,
    [STEP.ONE]:
      !!payloadCategory &&
      mandatoryCount === filledMandatoryCount &&
      hyperlinksValid,
    [STEP.TWO]: amountNumber > ZERO && isSettleTypeValid,
    [STEP.THIRD]:
      !!frequency && !!occurrences && !!slot && !!startDate && !!endDate,
    [STEP.FOUR]: true,
  };
};

/*                                 COMPONENT                                  */

const ScheduleBeneficiaryTransactionCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const dateRef = useRef(null);
  const endDateRef = useRef(null);
  const dropdownRef = useRef();
  const slotdropdownRef = useRef();
  const yearDropdownRef = useRef(null);
  const timersRef = useRef([]);
  const labelActionDispatchedRef = useRef(false);
  const prevBeneAccountRef = useRef(null);

  const currentAccountDetails = location?.state;

  /* -------------------------------- state -------------------------------- */
  // stepper
  const [activeStep, setActiveStep] = useState(STEP.ZERO);
  const [direction, setDirection] = useState(null);
  const [isStepAnimationComplete, setIsStepAnimationComplete] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // account step
  const [openOwner, setOpenOwner] = useState(false);
  const [activeType, setActiveType] = useState("pan");
  const [isConfidential, setIsConfidential] = useState(false);
  const [remPan, setRemPan] = useState("");
  const [remAccount, setRemAccount] = useState("");
  const [remIFSC, setRemIFSC] = useState("");

  // additional-details step
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [additionalData, setAdditionalData] = useState({});
  const [hyperLink, setHyperLink] = useState({});
  const [timeData, setTimeData] = useState({});
  const [quantities, setQuantities] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [previewImages, setPreviewImages] = useState({});
  const [startDate, setStartDate] = useState({});
  const [endDate, setEndDate] = useState({});
  const [selectedValue, setSelectedValue] = useState({});
  const [selectedValueMonth, setSelectedValueMonth] = useState({});

  // amount step
  const [isNeft, setIsNeft] = useState(true);
  const [isRtgs, setIsRtgs] = useState(false);
  const [slideAnimation, setSlideAnimation] = useState(false);

  // frequency step
  const [isFrequencyDropdownOpen, setIsFrequencyDropdownOpen] = useState(false);
  const [isSlotDropdownOpen, setIsSlotDropdownOpen] = useState(false);
  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
  const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false);
  const [isOccurrencesModalOpen, setIsOccurrencesModalOpen] = useState(false);
  const [scheduleOccurrenceDates, setScheduleOccurrenceDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState(SLOT_DATA || []);

  // duplicate check
  const [hasSimilarTransaction, setHasSimilarTransaction] = useState(false);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);

  // popups
  const [isLabelModalOpen, setIsLabelModalOpen] = useState(false);
  const [isLabelLoading, setIsLabelLoading] = useState(false);
  const [isConfirmLeavePopupOpen, setIsConfirmLeavePopupOpen] = useState(false);
  const [activeAccountPopup, setActiveAccountPopup] = useState(null);

  const [txnPayLoad, setTxnPayLoad] = useState({
    pan: currentAccountDetails?.pan || "",
    beneAccountId: currentAccountDetails?.id || null,
    bankAccountName: currentAccountDetails?.bank_account_holder_full_name || "",
    bankAccountNo: currentAccountDetails?.bank_acc_no || "",
    bankAccountIFSC: currentAccountDetails?.ifsc_code || "",
    bankLogo: currentAccountDetails?.bank_name || "",
    scheduleName: "",
    categoryIds: currentAccountDetails?.category || "",
    amount: "",
    amountInWords: "",
    settleType: "",
    ownerId: null,
    ownerBankIFSC: null,
    ownerBankBalance: 0,
    ownerLastUpdated: null,
    ownerBankLogo: null,
    ownerBankAccountNo: null,
    ownerAccountName: null,
    ownerBankAccountName: null,
    selectedCategory: null,
    mergeCategory: null,
    destinationAccountBalance: null,
    destinationAcountOwnerId: null,
    isDestinationOwnerActive: null,
    destinationAcountOwnerLastUpdated: null,
    frequency: "",
    transaction_start_date: "",
    transaction_end_date: "",
    slot: "",
    occurrences: "",
    labels: currentAccountDetails?.labels || [],
    ownerLabels: [],
  });

  const [assignedCategoryIds, setAssignedCategoryIds] = useState(
    txnPayLoad.categoryIds ? txnPayLoad.categoryIds.split(",") : [],
  );
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [frequencyStartDate, setFrequencyStartDate] = useState(
    parseToDateOrNull(txnPayLoad.transaction_start_date),
  );
  const [frequencyEndDate, setFrequencyEndDate] = useState(
    parseToDateOrNull(txnPayLoad.transaction_end_date),
  );

  const [selectedLabelsCreate, setSelectedLabelsCreate] = useState(() =>
    uniqueList(txnPayLoad.labels, txnPayLoad.ownerLabels),
  );
  const [availableLabels, setAvailableLabels] = useState(() =>
    uniqueList(txnPayLoad.labels, txnPayLoad.ownerLabels),
  );

  /* ------------------------------- selectors ------------------------------ */
  const categories = useSelector(
    (state) => state.sharedResources?.categoriesData?.categories,
  );

  /* ------------------------------- derived -------------------------------- */
  const steps = [
    globalText?.SOURCE_AND_DESTINATION_ACCOUNT,
    globalText?.CATEGORY_AND_LABELS,
    globalText?.AMOUNT,
    globalText?.FREQUENCY,
    globalText?.CONFIRM_AND_CREATE,
  ];

  const animationClass =
    direction === "next" ? "animate-slide-next" : "animate-slide-prev";

  const safeCategories = Array.isArray(categories) ? categories : [];
  const unassignCategories = safeCategories.filter(
    (cat) => cat?.active === "Y",
  );
  const unassignlength = unassignCategories.filter(
    (cat) => !assignedCategoryIds?.includes(cat?.id),
  );
  const isCategorySelected = !!selectedCategory;
  const allLabels = uniqueList(txnPayLoad?.ownerLabels, txnPayLoad?.labels);

  // replaces the old "enable / disable Next" effect + state
  const isNextEnabled = useMemo(
    () => getStepValidity(txnPayLoad)[activeStep] ?? false,
    [activeStep, txnPayLoad],
  );

  /* ------------------------------ small helpers --------------------------- */
  // timeouts are tracked so they never fire after unmount
  const schedule = (callback, delay) => {
    timersRef.current.push(setTimeout(callback, delay));
  };

  const triggerStepAnimation = (nextDirection) => {
    setDirection(nextDirection);
    setIsStepAnimationComplete(false);
    schedule(
      () => setIsStepAnimationComplete(true),
      IntervalConfiguration?.intervalSemiMid,
    );
  };

  const goToStep = (nextStep, nextDirection) => {
    setActiveStep(nextStep);
    triggerStepAnimation(nextDirection);
  };

  const showAmountLimitToast = (comparison) =>
    CommonToast({
      text: (
        <>
          Transaction amount should be {comparison} <br /> INR 2 lakhs.
        </>
      ),
      icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      variant: "var(--error)",
    });

  const showErrorToast = (text) => {
    toast.dismiss();
    CommonToast({
      text,
      icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      variant: "var(--error)",
      buttonNeeded: false,
    });
  };

  /** Clears every value that belongs to the selected category. */
  const resetCategoryFields = () => {
    setAdditionalData({});
    setSelectedValue({ from: "Select", to: "Select" });
    setStartDate({});
    setEndDate({});
    setHyperLink({});
    setPreviewImages({});
    setUploadedFiles({});
    setTimeData({});
    setQuantities({});
    setSelectedValueMonth({});
  };

  /* ------------------------------ create / next --------------------------- */
  const buildDuplicateCheckPayload = () => ({
    page_no: ONE,
    page_item_count: PaginationConfiguration?.paginationSmall,
    amount: txnPayLoad?.amount,
    payment_method: txnPayLoad?.settleType?.toUpperCase(),
    beneficiary_account_id: txnPayLoad?.beneAccountId,
    owner_account_id: txnPayLoad?.ownerId,
    confidential_transaction: isConfidential
      ? "Show only confidential"
      : "Hide confidential",
  });

  const handleCreateScheduledTransaction = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const filesToUpload = Object.values(uploadedFiles).filter(Boolean);

      const uploadResponses = filesToUpload.length
        ? await Promise.all(
            filesToUpload.map((file) => dispatch(uploadFile({ file }))),
          )
        : undefined;

      if (uploadResponses?.some((res) => !uploadFile.fulfilled.match(res))) {
        showErrorToast("File upload failed. Please try again.");
        return;
      }

      const scheduledBeneficiaryTxnData = {
        amount: txnPayLoad.amount,
        owner_account_id: txnPayLoad.ownerId,
        beneficiary_account_id: txnPayLoad.beneAccountId,
        category_id: selectedCategory?.id,
        payment_method: txnPayLoad.settleType.toUpperCase(),
        scheduled_name: txnPayLoad.scheduleName,
        frequency: toApiFrequency(txnPayLoad.frequency),
        occurrences: txnPayLoad.occurrences,
        confidential_transaction: isConfidential,
        transaction_start_date: txnPayLoad.transaction_start_date,
        transaction_end_date: txnPayLoad.transaction_end_date,
        slot: txnPayLoad.slot,
        additional_data: {
          data: buildAdditionalDataPayload(
            selectedCategory?.configuration?.data,
            additionalData,
            uploadResponses,
          ),
        },
        type_of_transactions: "Beneficiary Transaction",
        labels: uniqueList(txnPayLoad.labels, txnPayLoad.ownerLabels),
      };

      const response = await dispatch(
        scheduledBeneficiaryCreate({ scheduledBeneficiaryTxnData }),
      );

      if (scheduledBeneficiaryCreate.fulfilled.match(response)) {
        const transactionId = response.payload?.transaction?.[0]?.txn_id;
        navigate(
          `/beneficiary_transaction_dashboard/create_schedule/success/${transactionId}`,
        );
        return;
      }

      sessionStorage.setItem(
        "schedule_txn_error",
        JSON.stringify(response.payload),
      );
      navigate("/beneficiary_transaction_dashboard/create_schedule/failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Frequency -> Confirm: look for similar transactions before moving on. */
  const handleFrequencyToConfirm = async () => {
    if (isCheckingDuplicates) return;
    setIsCheckingDuplicates(true);
    setHasSimilarTransaction(false);

    try {
      const response = await dispatch(
        scheduledBeneficiaryViewAll(buildDuplicateCheckPayload()),
      );

      if (!response?.payload?.status) {
        showErrorToast("Couldn't validate the transaction. Please try again.");
        return;
      }

      const hasDuplicates = response.payload.transactions?.length > ZERO;
      setHasSimilarTransaction(hasDuplicates);

      if (hasDuplicates) {
        toast.dismiss();
        CommonToast({
          text: (
            <>
              Similar transaction found. Click check <br />
              to view.
            </>
          ),
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: true,
          btnProps: {
            enabledBgColor: "var(--negative-card)",
            enabledTextColor: "var(--text-primary)",
            btnText: "Check",
            imgColor: "var(--text-primary)",
            handleFunction: async () => {
              setIsSimilarModalOpen(true);
            },
            icon: ICONS.ARROW_RIGHT_WHITE,
            btnType: "button",
            isDisabled: false,
            hoverOption: "black",
          },
        });
      }

      goToStep(STEP.FOUR, "next");
    } finally {
      setIsCheckingDuplicates(false);
    }
  };

  const handleNext = async () => {
    if (isButtonDisabled) return;
    setIsButtonDisabled(true);

    try {
      if (activeStep === STEP.THIRD) {
        await handleFrequencyToConfirm();
        return;
      }

      if (activeStep < LAST_STEP_INDEX) {
        goToStep(Math.min(activeStep + ONE, LAST_STEP_INDEX), "next");
        return;
      }

      if (hasSimilarTransaction) {
        setIsSimilarModalOpen(true);
        return;
      }
      await handleCreateScheduledTransaction();
    } finally {
      setIsButtonDisabled(false);
    }
  };

  const isFormDirty = () =>
    txnPayLoad.ownerId !== null ||
    txnPayLoad.beneAccountId !== (currentAccountDetails?.id || null) ||
    txnPayLoad.scheduleName !== "" ||
    txnPayLoad.amount !== "" ||
    txnPayLoad.frequency !== "" ||
    !!selectedCategory;

  const handleBack = () => {
    if (activeStep === STEP.ZERO) {
      if (isFormDirty()) setIsConfirmLeavePopupOpen(true);
      else navigate(-1);
      return;
    }
    goToStep(Math.max(activeStep - ONE, ZERO), "previous");
  };

  /* ------------------------------- accounts ------------------------------- */
  const handleOwnerSelection = (name, acc, ifsc, logo, ownerId) =>
    setTxnPayLoad((prev) => ({
      ...prev,
      ownerId,
      // both keys are set so whichever one the shared components read is filled
      ownerAccountName: name,
      ownerBankAccountName: name,
      ownerBankAccountNo: acc,
      ownerBankIFSC: ifsc,
      ownerBankLogo: logo,
    }));

  const handleOpenDestinationAccountPopup = () =>
    setActiveAccountPopup("destination");

  const handleCloseAccountPopup = () => {
    setActiveAccountPopup(null);
    setRemPan("");
    setRemAccount("");
    setRemIFSC("");
  };

  const handleDestinationAccountSelection = (dataItem) => {
    if (!dataItem?.id) return;

    const {
      id: beneAccountId,
      name: accountName,
      bank_acc_no: rawAccountNumber,
      ifsc_code: rawIFSC,
      bank_name: bankLogo,
      category_ids: categoryIds,
      pan_no: panNo,
      labels,
    } = dataItem;

    setAssignedCategoryIds(
      (Array.isArray(categoryIds) ? categoryIds : []).map(
        (item) => item?.category_id ?? item?.id,
      ),
    );
    setSelectedCategory(null);
    resetCategoryFields();

    // a different destination invalidates the amount, method and duplicate check
    setIsNeft(true);
    setIsRtgs(false);
    setHasSimilarTransaction(false);

    setTxnPayLoad((prev) => ({
      ...prev,
      bankAccountName: accountName?.trim() || "",
      beneAccountId,
      bankAccountIFSC: rawIFSC?.trim() || "",
      bankAccountNo: rawAccountNumber?.trim() || "",
      bankLogo,
      pan: panNo,
      labels: labels || [],
      selectedCategory: null,
      mergeCategory: null,
      amount: "",
      amountInWords: "",
      settleType: "",
    }));

    handleCloseAccountPopup();
  };

  const handleUnassignNavigate = (id) =>
    navigate(
      `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/edit_categories/${id}`,
      { replace: false },
    );

  /* -------------------------------- labels -------------------------------- */
  const handleCloseLabelModal = (finalLabels) => {
    setIsLabelModalOpen(false);
    if (!finalLabels) return;

    setTxnPayLoad((prev) => ({
      ...prev,
      ...splitLabels(finalLabels, prev?.ownerLabels || []),
    }));
  };

  /* ----------------------------- category / details ----------------------- */
  const handleCategorySelect = (categoryId) => {
    const categoryData = safeCategories.find((c) => c.id === categoryId);
    const isAssigned = assignedCategoryIds?.includes(categoryId);

    if (!categoryData || categoryData?.active !== "Y" || !isAssigned) {
      setSelectedCategory(null);
      setTxnPayLoad((prev) => ({
        ...prev,
        selectedCategory: null,
        mergeCategory: null,
      }));
      setAdditionalData({});
      return;
    }

    // First render: show loader and clear the old fields
    setIsCategoryLoading(true);
    setTxnPayLoad((prev) => ({ ...prev, mergeCategory: null }));
    resetCategoryFields();

    // Second render: set the new category + fields
    requestAnimationFrame(() => {
      setSelectedCategory(categoryData);
      setTxnPayLoad((prev) => ({
        ...prev,
        selectedCategory: categoryData,
        mergeCategory: categoryData?.configuration?.data || [],
      }));
      setIsCategoryLoading(false);
    });
  };

  const handleInputChange = (event) => {
    event?.preventDefault?.();

    const source = event?.target || event;
    const { name, value } = source;
    // the field components send the type as `data_typeChan`
    const dataTypeFromEvent = source.data_type ?? source.data_typeChan;

    const field = txnPayLoad?.selectedCategory?.configuration?.data?.find(
      (item) => item.name === name,
    );
    if (!field) return;

    setAdditionalData((prev) => ({
      ...prev,
      [name]: toAdditionalDataValue(
        dataTypeFromEvent || field.data_type,
        value,
      ),
    }));
  };

  /* -------------------------------- amount -------------------------------- */
  const handleAmountChange = (input) => {
    const { name, value } = input?.target ? input.target : input;
    const trimmedValue = value?.toString()?.trim() ?? "";

    if (!trimmedValue) {
      setTxnPayLoad((prev) => ({ ...prev, amount: "", amountInWords: "" }));
      return;
    }
    if (trimmedValue[0] === "0") return;

    const numericValue = trimmedValue.replace(/[^0-9.]/g, "");

    if (
      name !== "amount" ||
      (!Validation.allowedAmount(numericValue) && numericValue.length)
    ) {
      return;
    }

    const settleType =
      parseFloat(numericValue) > RTGS_MIN_AMOUNT ? "rtgs" : "neft";
    const hasPaymentMethodChanged =
      (settleType === "neft" && isRtgs) || (settleType === "rtgs" && isNeft);

    if (hasPaymentMethodChanged) {
      setSlideAnimation(true);
      schedule(
        () => setSlideAnimation(false),
        PaymentMethodAnimationConfiguration.slideDuration,
      );
    }

    setIsNeft(settleType === "neft");
    setIsRtgs(settleType === "rtgs");
    setTxnPayLoad((prev) => ({
      ...prev,
      amount: numericValue,
      amountInWords: getAmountWords(numericValue),
      settleType,
    }));
  };

  const handleNeftClick = () => {
    if (Number(txnPayLoad?.amount) > RTGS_MIN_AMOUNT)
      showAmountLimitToast("less than");
  };

  const handleRtgsClick = () => {
    if (Number(txnPayLoad?.amount) <= RTGS_MIN_AMOUNT)
      showAmountLimitToast("more than");
  };

  /* ------------------------------- frequency ------------------------------ */
  const getEndDate = () =>
    getFrequencyEndDate(
      txnPayLoad?.transaction_start_date,
      txnPayLoad?.frequency,
    );

  const handleStartDateConfirm = () => {
    setAvailableSlots(getAvailableSlots(frequencyStartDate));
    setIsStartDatePickerOpen(false);

    const formattedStartDate = toLocalIsoDate(frequencyStartDate);
    setTxnPayLoad((prev) => ({
      ...prev,
      transaction_start_date: formattedStartDate,
      // only clear the slot when the start date really changed
      ...(prev.transaction_start_date !== formattedStartDate && { slot: "" }),
      ...(prev?.frequency === "Once" && {
        transaction_end_date: formattedStartDate,
      }),
    }));
  };

  const handleEndDateConfirm = () => {
    setIsEndDatePickerOpen(false);
    setTxnPayLoad((prev) => ({
      ...prev,
      transaction_end_date: toLocalIsoDate(frequencyEndDate),
    }));
  };

  // shared by both pickers: cancelling either one clears the whole date range
  const resetFrequencyDates = () => {
    setFrequencyStartDate(null);
    setFrequencyEndDate(null);
    setScheduleOccurrenceDates([]);
    setTxnPayLoad((prev) => ({
      ...prev,
      transaction_start_date: null,
      transaction_end_date: null,
      occurrences: "",
    }));
  };

  const handleStartDateCancel = () => {
    setIsStartDatePickerOpen(false);
    resetFrequencyDates();
  };

  const handleEndDateCancel = () => {
    setIsEndDatePickerOpen(false);
    resetFrequencyDates();
  };

  /* -------------------------------- effects ------------------------------- */
  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: false }));
  }, [dispatch]);

  // clear any pending timeouts on unmount
  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    },
    [],
  );

  useEffect(() => {
    const { beneAccountId } = txnPayLoad || {};
    if (!beneAccountId) return;
    if (prevBeneAccountRef.current === beneAccountId) return;

    prevBeneAccountRef.current = beneAccountId;
    dispatch(getAllCategories({ bene_account_id: beneAccountId }));
  }, [txnPayLoad?.beneAccountId]);

  // entered values -> mergeCategory
  useEffect(() => {
    if (!additionalData) return;

    const fields = txnPayLoad.selectedCategory?.configuration?.data;
    setTxnPayLoad((prev) => ({
      ...prev,
      mergeCategory: buildMergeCategory(fields, additionalData),
    }));
  }, [additionalData, txnPayLoad.selectedCategory]);

  // seed label suggestions from payload + session storage
  useEffect(() => {
    setAvailableLabels(
      uniqueList(
        txnPayLoad?.labels,
        txnPayLoad?.ownerLabels,
        readSessionJson("scheduleBeneficiaryTxnLabels"),
        readSessionJson("beneficiaryAccountLabels"),
      ),
    );
  }, []);

  // owner labels first, then the remaining labels
  useEffect(() => {
    const ownerLabels = txnPayLoad?.ownerLabels || [];
    const otherLabels = txnPayLoad?.labels || [];

    setSelectedLabelsCreate([
      ...ownerLabels,
      ...otherLabels.filter((label) => !ownerLabels.includes(label)),
    ]);
  }, [txnPayLoad?.ownerLabels, txnPayLoad?.labels]);

  // payload dates -> date picker state
  useEffect(() => {
    setFrequencyStartDate(
      parseToDateOrNull(txnPayLoad?.transaction_start_date),
    );
    setFrequencyEndDate(parseToDateOrNull(txnPayLoad?.transaction_end_date));
  }, [txnPayLoad?.transaction_start_date, txnPayLoad?.transaction_end_date]);

  useEffect(() => {
    if (frequencyStartDate) handleStartDateConfirm();
  }, [frequencyStartDate]);

  useEffect(() => {
    if (frequencyEndDate) handleEndDateConfirm();
  }, [frequencyEndDate]);

  // slots + occurrences depend on the chosen schedule
  useEffect(() => {
    setAvailableSlots(getAvailableSlots(frequencyStartDate));

    const {
      transaction_start_date: start,
      transaction_end_date: end,
      frequency,
    } = txnPayLoad;
    if (!start || !end || !frequency) return;

    // `ignore` drops responses that were superseded by a newer date change
    let ignore = false;

    (async () => {
      const response = await dispatch(
        scheduledBeneficiaryOccurrences({
          paramsData: {
            frequency: toApiFrequency(frequency),
            transaction_start_date: toLocalIsoDate(start),
            transaction_end_date: toLocalIsoDate(end),
          },
        }),
      );
      if (ignore) return;

      setTxnPayLoad((prev) => ({
        ...prev,
        occurrences: response?.payload?.occurrences ?? "",
      }));
      setScheduleOccurrenceDates(response?.payload?.dates ?? []);
    })();

    return () => {
      ignore = true;
    };
  }, [
    txnPayLoad?.transaction_start_date,
    txnPayLoad?.transaction_end_date,
    txnPayLoad?.frequency,
  ]);

  // close dropdowns / pickers on outside click
  useEffect(() => {
    const isOutside = (ref, target) =>
      ref.current && !ref.current.contains(target);

    const handleClickOutside = (event) => {
      if (isOutside(dropdownRef, event.target))
        setIsFrequencyDropdownOpen(false);
      if (isOutside(slotdropdownRef, event.target))
        setIsSlotDropdownOpen(false);
      if (isOutside(dateRef, event.target)) setIsStartDatePickerOpen(false);
      if (isOutside(endDateRef, event.target)) setIsEndDatePickerOpen(false);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  /* ------------------------------ stepper data ---------------------------- */
  const dataArray = steps.map((stepLabel, index) => {
    const isLastStep = index === LAST_STEP_INDEX;
    const isTxnLoading = isLastStep && isSubmitting;

    let nextText = "Next";
    if (isLastStep) nextText = hasSimilarTransaction ? "Check" : "Create";

    let nextEnableBgColor = "var(--text-primary)";
    if (isLastStep) {
      nextEnableBgColor = hasSimilarTransaction
        ? "var(--emphasis)"
        : "var(--positive-act)";
    }

    return {
      ...COMMON_STEPPER_CONFIG,
      previousText: index === STEP.ZERO ? "Cancel" : "Previous",
      nextText,
      nextEnableBgColor,
      nextDisableBgColor: isTxnLoading
        ? "var(--positive-act)"
        : "var(--text-disabled)",
      nextEnableTextColor: isLastStep
        ? "var(--text-primary)"
        : "var(--primary-bg)",
      nextLoaderNeeded: isLastStep,
      nextIsLoading: isTxnLoading,
      nextIsEnabled: isLastStep
        ? !isSubmitting
        : isNextEnabled && !isCheckingDuplicates && !isButtonDisabled,
      stepperText: stepLabel,
    };
  });

  /* ------------------------------- renderers ------------------------------ */
  const renderStep = () => {
    switch (activeStep) {
      case STEP.ZERO:
        return (
          <ScheduleTransactionStepSelectAccount
            txnPayLoad={txnPayLoad}
            setTxnPayLoad={setTxnPayLoad}
            openOwner={openOwner}
            setOpenOwner={setOpenOwner}
            handleOwnerSelection={handleOwnerSelection}
            handleOpenDestinationAccountPopup={
              handleOpenDestinationAccountPopup
            }
            isConfidential={isConfidential}
            setIsConfidential={setIsConfidential}
          />
        );

      case STEP.ONE:
        return (
          <ScheduleTransactionStepAdditionalDetails
            txnPayLoad={txnPayLoad}
            beneAccountId={txnPayLoad?.beneAccountId}
            category={assignedCategoryIds}
            selectedCategory={selectedCategory}
            unassignCategories={unassignCategories}
            unassignlength={unassignlength}
            disableCategories={DISABLE_CATEGORIES}
            isCategoryLoading={isCategoryLoading}
            isCategorySelected={isCategorySelected}
            handleCategorySelect={handleCategorySelect}
            handleUnassignNavigate={handleUnassignNavigate}
            additionalData={additionalData}
            setAdditionalData={setAdditionalData}
            handleInputChange={handleInputChange}
            hyperLink={hyperLink}
            setHyperLink={setHyperLink}
            timeData={timeData}
            setTimeData={setTimeData}
            quantities={quantities}
            setQuantities={setQuantities}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            selectedValue={selectedValue}
            setSelectedValue={setSelectedValue}
            selectedValueMonth={selectedValueMonth}
            setSelectedValueMonth={setSelectedValueMonth}
            previewImages={previewImages}
            setPreviewImages={setPreviewImages}
            selectedLabelsCreate={selectedLabelsCreate}
            setShowLabelModal={setIsLabelModalOpen}
          />
        );

      case STEP.TWO:
        return (
          <ScheduleTransactionStepAmount
            txnPayLoad={txnPayLoad}
            isNeft={isNeft}
            isRtgs={isRtgs}
            slideAnimation={slideAnimation}
            handleAmountChange={handleAmountChange}
            handleNeftClick={handleNeftClick}
            handleRtgsClick={handleRtgsClick}
          />
        );

      case STEP.THIRD:
        return (
          <ScheduleTransactionStepFrequency
            txnPayLoad={txnPayLoad}
            setTxnPayLoad={setTxnPayLoad}
            setIsOccurrencesModalOpen={setIsOccurrencesModalOpen}
            SlotMappingData={availableSlots}
            frequencyOptions={FREQUENCY_OPTIONS}
            frequencyStartDate={frequencyStartDate}
            frequencyEndDate={frequencyEndDate}
            setFrequencyStartDate={setFrequencyStartDate}
            setFrequencyEndDate={setFrequencyEndDate}
            showDatePicker={isStartDatePickerOpen}
            setShowDatePicker={setIsStartDatePickerOpen}
            showEndDatePicker={isEndDatePickerOpen}
            setShowEndDatePicker={setIsEndDatePickerOpen}
            dropdownOpen={isFrequencyDropdownOpen}
            setDropdownOpen={setIsFrequencyDropdownOpen}
            slotdropdownOpen={isSlotDropdownOpen}
            setSlotDropdownOpen={setIsSlotDropdownOpen}
            dateRef={dateRef}
            endDateRef={endDateRef}
            yearDropdownRef={yearDropdownRef}
            formatDate={formatDisplayDate}
            handleDateChange={setFrequencyStartDate}
            handleEndDateChange={setFrequencyEndDate}
            handleConfirm={handleStartDateConfirm}
            handleCancel={handleStartDateCancel}
            handleEndConfirm={handleEndDateConfirm}
            handleEndCancel={handleEndDateCancel}
            getEndDate={getEndDate}
            weekdays={WEEKDAYS}
            months={SHORT_MONTHS}
          />
        );

      case STEP.FOUR:
        return (
          <ScheduleTransactionStepConfirm
            txnPayLoad={txnPayLoad}
            setTxnPayLoad={setTxnPayLoad}
            isConfidential={isConfidential}
            previewImages={previewImages}
            uploadedFiles={uploadedFiles}
            allLabels={allLabels}
          />
        );

      default:
        return null;
    }
  };

  /* --------------------------------- render ------------------------------- */
  return (
    <>
      {activeAccountPopup && (
        <BeneficiaryAccountViewAllPopup
          id={txnPayLoad?.beneAccountId}
          type="viewAll"
          flow="beneficiaryTxn"
          accountType="beneficiary"
          flowType="scheduleCreate"
          sourceDestiAcc
          ispanbankRequired={false}
          onClose={handleCloseAccountPopup}
          handleButtonClick={handleDestinationAccountSelection}
          activeType={activeType}
          setActiveType={setActiveType}
          remPan={remPan}
          setRemPan={setRemPan}
          remAccount={remAccount}
          setRemAccount={setRemAccount}
          remIFSC={remIFSC}
          setRemIFSC={setRemIFSC}
          typePanBank={activeType}
          beneAccountId={txnPayLoad?.beneAccountId}
          setTxnPayLoad={setTxnPayLoad}
          txnPayLoad={txnPayLoad}
          setIsNeft={setIsNeft}
          setIsRtgs={setIsRtgs}
          showModalPopup={false}
          createDisabled={false}
        />
      )}

      {isCheckingDuplicates && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-fixed-ui bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <div>
            <Loader type="bulk_upload" />
            <p className="font-semibold text-font-lg text-text-primary">
              Validating the data
            </p>
          </div>
        </div>
      )}

      {isSimilarModalOpen && (
        <ScheduleBeneficiaryTransactionViewAllPopup
          onClose={() => setIsSimilarModalOpen(false)}
          headerTitle="Similar transactions found"
          headerIcon={ICONS.ALERT}
          headerSubtitle="Transactions with similar details have already been created. Proceeding will result in a duplicate transaction."
          showFooter
          footerTitle="Proceed with creating this transaction?"
          showFooterButton
          footerButtonText="Proceed with caution"
          onFooterButtonClick={() => {
            setIsSimilarModalOpen(false);
            handleCreateScheduledTransaction();
          }}
          contentProps={{
            isPopup: true,
            type: "beneficiaryScheduleCreateTxn",
            duplicateFilters: buildDuplicateCheckPayload(),
            destination_account_number: txnPayLoad?.bankAccountNo,
            source_account_number: txnPayLoad?.ownerBankAccountNo,
            txnPayLoad,
          }}
        />
      )}

      {isConfirmLeavePopupOpen && (
        <div
          className="fixed z-z-index-max flex items-center justify-center top-top-base left-left-base w-width-full h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <SmallPopup
            headerTitle={globalText?.LEAVE_PAGE_WARNING_HEADER}
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-lg"
            subtext={globalText?.EXIT_CURRENT_FLOW_NOTE}
            btnOneText={globalText?.STAY}
            btnOneHandleFunction={() => setIsConfirmLeavePopupOpen(false)}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption="white"
            btnTwoText={globalText?.LEAVE}
            btnTwoHandleFunction={() => {
              setIsConfirmLeavePopupOpen(false);
              navigate(-1);
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={false}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
          />
        </div>
      )}

      {isLabelModalOpen && (
        <div className="fixed left-left-base w-width-full top-top-base h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity flex items-center justify-center z-max-zindex">
          <CommonAssignLabelPopup
            headerTitle="Assign/unassign labels:"
            headersubtext="Labels will be used to generate statistics for the associated transactions."
            closehandleFunction={handleCloseLabelModal}
            closeisDisabled={false}
            labels={availableLabels}
            setLabels={setAvailableLabels}
            isLabelPopupOpen={isLabelModalOpen}
            setPayload={setTxnPayLoad}
            txnPayLoad={txnPayLoad}
            selectedLabelsCreate={selectedLabelsCreate}
            setSelectedLabelsCreate={setSelectedLabelsCreate}
            labelActionDispatchedRef={labelActionDispatchedRef}
            loading={isLabelLoading}
            setLoading={setIsLabelLoading}
            type="create"
          />
        </div>
      )}

      {isOccurrencesModalOpen && (
        <div className="z-z-index-max">
          <ScheduleTxnOccurrencesPopup
            closeCallback={() => setIsOccurrencesModalOpen(false)}
            occurrences={scheduleOccurrenceDates}
          />
        </div>
      )}

      <div className="h-screen flex-col flex overflow-hidden">
        <div className="w-width-full hidden Qsmd:block h-navbar-breadcrumb-height Qlg:bg-transparent bg-primary-nav flex-shrink-0"></div>
        <div className="fixed Qsmd:relative left-left-base z-z-index-loader flex justify-center items-center flex-col lg:flex-col-reverse w-width-full lg:py-padding-base bottom-bottom-base top-auto lg:top-top-base lg:bottom-auto Qsmd:bg-transparent shrink-0 Qsmd:min-h-[calc(var(--stepper-height)+var(--icon-sm))] min-h-stepper-moble-height">
          <PrimaryStepper
            activeStep={activeStep}
            handleForwardAction={handleNext}
            handleBackwardAction={handleBack}
            dataArray={dataArray}
          />
        </div>

        {!isOccurrencesModalOpen && (
          <div className="fixed top-top-base left-left-base mt-margin-base w-width-full rounded-bl-border-radius-md rounded-br-border-radius-md z-z-index-header bg-primary-nav shadow-box-shadow-bottom lg:hidden block">
            <TopNavComponent
              topNavHeader={globalText?.CREATE_SCHEDULE_TRANSACTION}
              topNavSubText={steps[activeStep] || ""}
              isFormattingNotRequired
            />
          </div>
        )}

        <div
          className={`flex flex-col h-height-full items-center relative w-width-full flex-1 overflow-y-auto overflow-x-hidden ${activeStep === STEP.ONE ? "mb-[var(--stepper-moble-height)] Qmd:mb-margin-base" : ""}`}
          style={{ scrollbarWidth: "none" }}
        >
          <div className="max-w-screen-Qlg mx-auto flex flex-col w-width-full">
            <div className="w-width-full">
              <div className="h-navbar-height Qsmd:hidden block"></div>
            </div>
            <div
              className={`w-width-full ${
                (activeStep === STEP.ZERO && direction === "next") ||
                isStepAnimationComplete
                  ? ""
                  : animationClass
              }`}
            >
              {renderStep()}
            </div>
          </div>
        </div>

        {activeStep !== STEP.ONE && (
          <div className="h-stepper-moble-height w-width-full Qsmd:hidden block"></div>
        )}
      </div>
    </>
  );
};

export default ScheduleBeneficiaryTransactionCreate;
